import React, { ChangeEvent, FunctionComponent, useState } from "react";
import { connect, MapStateToProps } from "react-redux";

import { ButtonBackgroundColor, Caption, CheckBoxComponentValue, ColSpan, Divider, GridColumnCount, GridGapSize, GridLayout, GridLayoutItem, IconButton, Select, SelectBody, SelectItem, Text, VerticalDivider, WhiteSpace } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { getSupportedLanguages } from "@multiweb/reactCore/i18n/selectors";
import { bemClassNames } from "@multiweb/reactUtils";
import { IAttrValue, IconCode, ILanguageExtended } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { LOC_ADD, LOC_CHOOSE_FROM_PARTICIPANTS, LOC_CODE_NAME, LOC_CONFIRMATION_MESSAGE, LOC_DISPLAY, LOC_EXAMPLE, LOC_FORM_OPENING, LOC_ICON, LOC_LANGUAGE, LOC_MENU_TEXT, LOC_OBJECT_BINDING, LOC_OTHER, LOC_PARAMETRIC_FORM, LOC_PARENT_MENU_ITEM, LOC_REMOVE, LOC_SETTING_A_VALUE_FOR_THE_PARAMETRIC_FORM, LOC_SUITABLE_FOR_MASS_LAUNCH, LOC_VALIDATE_DEFERRED_REQUIRED_FIELDS, LOC_VALIDATE_THE_RECOMMENDED_FIELDS } from "../../../../../../locKeys";
import { isWorkflowReadOnly } from "../../../../../../selectors";
import { WorkflowEditorProps } from "../../../../../../types";
import { parseStringToBoolean } from "../../../../../../utils";
import { Text as MenuText } from "../../../../../../workflowModel/Triggers";
import { CONST_MENU_CAPTIONS_EL, CONST_MENU_CODE_NAME, CONST_MENU_CONFIRM_MESSAGES_EL, CONST_MENU_ICON_NAME, CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT, CONST_MENU_MASTER_NODE_AT, CONST_MENU_MULTI_ACTION_ENABLE_AT, CONST_MENU_PARAM_FORM_NAME_AT, CONST_MENU_PARTICIPANTS_SELECTING_AT, CONST_MENU_SET_VALUES_AT, CONST_MENU_VALIDATE_POST_REQ_AT, CONST_MENU_VALIDATE_RECOMMENDED_AT } from "../../../../../../workflowParser/constants";
import { Edit, EditableItem, Memo, TextLOVEdit, WfCheckBox } from "../../../../components";
import { MAX_INPUT_LENGTH_L, MAX_INPUT_LENGTH_S } from "../constants";
import { TriggerPageProps } from "../types";

type MenuPageComponentProps = {
    readonly supportedLanguages: readonly ILanguageExtended[];
    readonly isWorkflowReadOnly: boolean;
    readonly exampleText: string;
    readonly addText: string;
    readonly languageText: string;
    readonly menuText: string;
    readonly confirmationMessageText: string;
    readonly removeText: string;
} & TriggerPageProps;

const getFormClassAttrs = ["Kod_formulare"];

const classNames = bemClassNames("menu-page-component");
const languageCell = classNames("language-cell");
const textInMenuCell = classNames("text-in-menu-cell");
const confirmationMessageCell = classNames("confirmation-message-cell");
const headerContainer = classNames("header-container");
const captionsContainerClassName = classNames("captions-container");
const contentContainerClassName = classNames("content-container");
const buttonsContainer = classNames("buttons-container");
const checkboxesContainer = classNames("checkboxes-container");
const triggersTabRowClass = classNames("triggers-tab-row");
const parametricFormClass = classNames("parametric-form-input");
const objectBindingClass = classNames("object-binding-input");
const colClass = classNames("col");
const groupBox = classNames("group-box");
const groupBoxCaption = classNames("group-box-caption");

const MenuPageComponent: FunctionComponent<MenuPageComponentProps> = ({
    hostKey,
    properties,
    supportedLanguages,
    isWorkflowReadOnly,
    updateTrigger,
    exampleText,
    addText,
    languageText,
    menuText,
    removeText,
    confirmationMessageText,
}) => {
    const jsonCaptions = properties[CONST_MENU_CAPTIONS_EL];
    const jsonMessages = properties[CONST_MENU_CONFIRM_MESSAGES_EL];
    const captions: MenuText[] = jsonCaptions == null ? [] : JSON.parse(jsonCaptions);
    const confirmMessages: MenuText[] = jsonMessages == null ? [] : JSON.parse(jsonMessages);
    const filteredSupportedLanguages = supportedLanguages.filter(({ code }) => captions.every(caption => code !== caption.lang));

    const [selectedLangIndex, setSelectedLangIndex] = useState<number | undefined>(undefined);

    const onLanguageSelected = (value: string | undefined) => {
        captions.push({ lang: value ?? "", text: "" });
        confirmMessages.push({ lang: value ?? "", text: "" });
        updateTrigger([
            { key: CONST_MENU_CAPTIONS_EL, value: JSON.stringify(captions) },
            { key: CONST_MENU_CONFIRM_MESSAGES_EL, value: JSON.stringify(confirmMessages) },
        ]);
        setSelectedLangIndex(captions.length - 1);
    };

    const onRemoveLanguageButtonClick = () => {
        if (selectedLangIndex != null) {
            const newCaptions = captions.filter((_caption, index) => index !== selectedLangIndex);
            const newConfirmMessages = confirmMessages.filter((_message, index) => index !== selectedLangIndex);
            updateTrigger([
                { key: CONST_MENU_CAPTIONS_EL, value: JSON.stringify(newCaptions) },
                { key: CONST_MENU_CONFIRM_MESSAGES_EL, value: JSON.stringify(newConfirmMessages) },
            ]);
            setSelectedLangIndex(undefined);
        }
    };

    const getFormClass = (results: readonly IAttrValue[] | undefined) => {
        if (results && results.length > 0)
            updateTrigger([{ key: CONST_MENU_PARAM_FORM_NAME_AT, value: results[0].toString() }]);
    };

    const onCaptionsChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        captions[index] = { lang: captions[index].lang, text: e.target.value };
        updateTrigger([{ key: CONST_MENU_CAPTIONS_EL, value: JSON.stringify(captions) }]);
    };

    const onConfirmMessageChange = (index: number, e: ChangeEvent<HTMLTextAreaElement>) => {
        confirmMessages[index] = { lang: confirmMessages[index].lang, text: e.target.value };
        updateTrigger([{ key: CONST_MENU_CONFIRM_MESSAGES_EL, value: JSON.stringify(confirmMessages) }]);
    };

    const onCodenameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateTrigger([{ key: CONST_MENU_CODE_NAME, value: e.target.value }]);
    };

    const onIconChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateTrigger([{ key: CONST_MENU_ICON_NAME, value: e.target.value }]);
    };

    const onChooseFromParticipantsChange = (value: CheckBoxComponentValue) => {
        updateTrigger([{ key: CONST_MENU_PARTICIPANTS_SELECTING_AT, value: value ? "true" : "false" }]);
    };

    const onMultiActionEnableChange = (value: CheckBoxComponentValue) => {
        updateTrigger([{ key: CONST_MENU_MULTI_ACTION_ENABLE_AT, value: value ? "true" : "false" }]);
    };

    const onValidatePostReqChange = (value: CheckBoxComponentValue) => {
        updateTrigger([{ key: CONST_MENU_VALIDATE_POST_REQ_AT, value: value ? "true" : "false" }]);
    };

    const onValidateRecommendedChange = (value: CheckBoxComponentValue) => {
        updateTrigger([{ key: CONST_MENU_VALIDATE_RECOMMENDED_AT, value: value ? "true" : "false" }]);
    };

    const onMasterNodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateTrigger([{ key: CONST_MENU_MASTER_NODE_AT, value: e.target.value }]);
    };

    const onParamFormNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateTrigger([{ key: CONST_MENU_PARAM_FORM_NAME_AT, value: e.target.value }]);
    };

    const onLinkAttrForSelObjChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateTrigger([{ key: CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT, value: e.target.value }]);
    };

    const onSetValuesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateTrigger([{ key: CONST_MENU_SET_VALUES_AT, value: e.target.value }]);
    };

    return (
        <GridLayout columns={GridColumnCount.one} gap={GridGapSize.two}>
            <GridLayoutItem colSpan={ColSpan.one}>
                <div>
                    <div className={groupBoxCaption}>
                        <Caption locKey={LOC_DISPLAY} />
                    </div>
                    <div className={groupBox}>
                        <GridLayout columns={GridColumnCount.one} gap={GridGapSize.two}>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <div className={triggersTabRowClass}>
                                    <div className={colClass}>
                                        <Caption locKey={LOC_ICON} />
                                        <Edit maxLength={MAX_INPUT_LENGTH_S} value={properties[CONST_MENU_ICON_NAME]} onChange={onIconChange} {...{ hostKey }} />
                                    </div>
                                    <div className={colClass}>
                                        <Caption locKey={LOC_PARENT_MENU_ITEM} />
                                        <Edit placeholder={"ROOT"} maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_MENU_MASTER_NODE_AT]} onChange={onMasterNodeChange} {...{ hostKey }} />
                                    </div>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <div className={captionsContainerClassName}>
                                    <div className={buttonsContainer}>
                                        <div>
                                            <Select
                                                options={filteredSupportedLanguages.map(({ code }) => {
                                                    return { text: code, value: code };
                                                })}
                                                optionsContent={({ opened, onSelect }) => {
                                                    return (opened && <SelectBody>
                                                        {filteredSupportedLanguages.map(({ code }) =>
                                                            <SelectItem key={code} onSelect={() => onSelect(code)} text={code} />,
                                                        )}
                                                    </SelectBody>);
                                                }}
                                                selectedOptionContent={({ opened, setOpened }) =>
                                                    <IconButton
                                                        disabled={isWorkflowReadOnly || filteredSupportedLanguages.length === 0}
                                                        iconCode={IconCode.uiPlus}
                                                        onClick={() => setOpened(!opened)}
                                                        title={addText}
                                                        backgroundColor={ButtonBackgroundColor.transparent} />
                                                }
                                                value={undefined}
                                                onChange={onLanguageSelected} />
                                        </div>
                                        <IconButton
                                            iconCode={IconCode.delete}
                                            disabled={isWorkflowReadOnly || selectedLangIndex == null}
                                            onClick={onRemoveLanguageButtonClick}
                                            title={removeText}
                                            backgroundColor={ButtonBackgroundColor.transparent} />
                                    </div>
                                    <Divider />
                                    <div className={headerContainer}>
                                        <div className={languageCell}>
                                            <Text value={languageText} title={languageText} ellipsis whiteSpace={WhiteSpace.nowrap} />
                                        </div>
                                        <VerticalDivider />
                                        <div className={textInMenuCell}>
                                            <Text value={menuText} title={menuText} ellipsis whiteSpace={WhiteSpace.nowrap} />
                                        </div>
                                        <VerticalDivider />
                                        <div className={confirmationMessageCell}>
                                            <Text value={confirmationMessageText} title={confirmationMessageText} ellipsis whiteSpace={WhiteSpace.nowrap} />
                                        </div>
                                    </div>
                                    <Divider />
                                    <div className={contentContainerClassName}>
                                        {captions.map(({ lang, text }, index) =>
                                            <EditableItem
                                                key={lang}
                                                isSelected={selectedLangIndex === index}
                                                textType={lang}
                                                caption={text}
                                                message={confirmMessages[index].text}
                                                onClick={() => setSelectedLangIndex(index)}
                                                onCaptionChange={(newCaption) => onCaptionsChange(index, newCaption)}
                                                onConfirmMessageChange={(newConfirmMessage) => onConfirmMessageChange(index, newConfirmMessage)}
                                                {...{ hostKey }} />,
                                        )}
                                    </div>
                                </div>
                            </GridLayoutItem>
                        </GridLayout>
                    </div>
                </div>
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.one}>
                <div>
                    <div className={groupBoxCaption}>
                        <Caption locKey={LOC_OTHER} />
                    </div>
                    <div className={groupBox}>
                        <GridLayout columns={GridColumnCount.one} gap={GridGapSize.two}>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <Caption locKey={LOC_CODE_NAME} />
                                <Edit maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_MENU_CODE_NAME]} onChange={onCodenameChange} {...{ hostKey }} />
                            </GridLayoutItem>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <div className={checkboxesContainer}>
                                    <WfCheckBox textLocKey={LOC_CHOOSE_FROM_PARTICIPANTS} showFalseAsCrossed value={parseStringToBoolean(properties[CONST_MENU_PARTICIPANTS_SELECTING_AT], false)} onChange={onChooseFromParticipantsChange} {...{ hostKey }} />
                                    <WfCheckBox textLocKey={LOC_SUITABLE_FOR_MASS_LAUNCH} showFalseAsCrossed value={parseStringToBoolean(properties[CONST_MENU_MULTI_ACTION_ENABLE_AT], false)} onChange={onMultiActionEnableChange} {...{ hostKey }} />
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <div className={checkboxesContainer}>
                                    <WfCheckBox textLocKey={LOC_VALIDATE_DEFERRED_REQUIRED_FIELDS} showFalseAsCrossed value={parseStringToBoolean(properties[CONST_MENU_VALIDATE_POST_REQ_AT], false)} onChange={onValidatePostReqChange} {...{ hostKey }} />
                                    <WfCheckBox textLocKey={LOC_VALIDATE_THE_RECOMMENDED_FIELDS} showFalseAsCrossed value={parseStringToBoolean(properties[CONST_MENU_VALIDATE_RECOMMENDED_AT], false)} onChange={onValidateRecommendedChange} {...{ hostKey }} />
                                </div>
                            </GridLayoutItem>
                        </GridLayout>
                    </div>
                </div>
            </GridLayoutItem>
            <GridLayoutItem colSpan={ColSpan.one}>
                <div>
                    <div className={groupBoxCaption}>
                        <Caption locKey={LOC_FORM_OPENING} />
                    </div>
                    <div className={groupBox}>
                        <GridLayout columns={GridColumnCount.one} gap={GridGapSize.two}>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <div className={triggersTabRowClass}>
                                    <div className={colClass}>
                                        <div className={parametricFormClass}>
                                            <Caption locKey={LOC_PARAMETRIC_FORM} />
                                            <TextLOVEdit
                                                value={properties[CONST_MENU_PARAM_FORM_NAME_AT]}
                                                maxLength={MAX_INPUT_LENGTH_L}
                                                onChange={onParamFormNameChange}
                                                formName="FRM.CFormular.CFormular"
                                                attributeNames={getFormClassAttrs}
                                                onFormClose={getFormClass}
                                                {...{ hostKey }} />
                                        </div>
                                    </div>
                                    <div className={colClass}>
                                        <div className={objectBindingClass}>
                                            <Caption locKey={LOC_OBJECT_BINDING} />
                                            <Edit maxLength={MAX_INPUT_LENGTH_L} value={properties[CONST_MENU_LINK_ATTR_FOR_SEL_OBJ_AT]} onChange={onLinkAttrForSelObjChange} {...{ hostKey }} />
                                        </div>
                                    </div>
                                </div>
                            </GridLayoutItem>
                            <GridLayoutItem colSpan={ColSpan.one}>
                                <Caption locKey={LOC_SETTING_A_VALUE_FOR_THE_PARAMETRIC_FORM} />
                                <Memo placeholder={exampleText} value={properties[CONST_MENU_SET_VALUES_AT]} onChange={onSetValuesChange} {...{ hostKey }} />
                            </GridLayoutItem>
                        </GridLayout>
                    </div>
                </div>
            </GridLayoutItem>
        </GridLayout>
    );
};

type StateProps = Pick<MenuPageComponentProps, "supportedLanguages" | "isWorkflowReadOnly" | "exampleText" | "addText" | "languageText" | "menuText" | "confirmationMessageText" | "removeText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    supportedLanguages: getSupportedLanguages(state),
    isWorkflowReadOnly: isWorkflowReadOnly(state, hostKey),
    exampleText: getPrimaryText(state, LOC_EXAMPLE) ?? "Ukázka: Nazev_Hodnoty1=hodnota1;Nazev_Hodnoty2=hodnota2;...",
    addText: getPrimaryText(state, LOC_ADD) ?? "",
    languageText: getPrimaryText(state, LOC_LANGUAGE) ?? "Jazyk",
    menuText: getPrimaryText(state, LOC_MENU_TEXT) ?? "Text v menu",
    confirmationMessageText: getPrimaryText(state, LOC_CONFIRMATION_MESSAGE) ?? "Potvrzovací hláška",
    removeText: getPrimaryText(state, LOC_REMOVE) ?? "Odebrat",
});

export const MenuPage = connect(mapStateToProps)(MenuPageComponent);
